import { Component, OnInit } from '@angular/core';
import { UserService } from 'src/app/services/user/user.service';
import * as alertifyjs from 'alertifyjs';

@Component({
  selector: 'app-carga-masiva-c004',
  templateUrl: './carga-masiva-c004.component.html',
  styleUrls: ['./carga-masiva-c004.component.css']
})
export class CargaMasivaC004Component implements OnInit {
  
  // Control de pestañas
  activeTab: string = 'carga'; // 'carga' o 'historial'
  
  // Estados del flujo
  step: number = 1; // 1: Instrucciones, 2: Carga, 3: Procesando, 4: Resultado
  
  // Confirmación
  confirmPermanent: boolean = false;
  
  // Drag & Drop
  isDragging: boolean = false;
  
  // Archivo
  selectedFile: File | null = null;
  fileName: string = '';
  filePreview: any[] = [];
  validationResult: any = null;
  
  // Progreso
  currentBatchId: string = '';
  currentTraceId: string = '';
  progress: number = 0;
  processingInterval: any = null;
  
  // Resultado
  batchResult: any = null;
  
  // Historial
  batches: any[] = [];
  loadingHistory: boolean = false;
  
  // Filtros historial
  filterStartDate: string = '';
  filterEndDate: string = '';
  filterStatus: string = '';
  currentPage: number = 1;
  totalPages: number = 1;
  
  // Loading states
  loadingTemplate: boolean = false;
  loadingValidation: boolean = false;
  loadingUpload: boolean = false;

  constructor(
    private _user: UserService
  ) { }

  ngOnInit(): void {
    this.loadBatchHistory();
  }

  ngOnDestroy(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
    }
  }

  // ============================================================
  // PASO 1: DESCARGAR PLANTILLA
  // ============================================================

  downloadTemplate = async () => {
    this.loadingTemplate = true;
    try {
      const response = await this._user.downloadC004Template();
      
      // Crear blob y descargar
      const blob = new Blob([response], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_carga_masiva_c004.xlsx';
      link.click();
      window.URL.revokeObjectURL(url);
      
      alertifyjs.success('Plantilla descargada correctamente');
    } catch (error: any) {
      console.error('Error descargando plantilla:', error);
      alertifyjs.error('Error al descargar la plantilla');
    }
    this.loadingTemplate = false;
  }

  goToConfirmation = () => {
    this.step = 2;
  }

  // ============================================================
  // PASO 2: CONFIRMACIÓN
  // ============================================================


  goToUpload = () => {
    this.step = 2;
  }

  backToInstructions = () => {
    this.step = 1;
    this.selectedFile = null;
    this.fileName = '';
    this.validationResult = null;
    this.filePreview = [];
    this.confirmPermanent = false;
    this.isDragging = false;
  }

  // ============================================================
  // PASO 3: CARGA DE ARCHIVO
  // ============================================================

  // ============================================================
  // DRAG & DROP HANDLERS
  // ============================================================

  onDragOver = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  removeFile = (event: Event) => {
    event.stopPropagation();
    this.selectedFile = null;
    this.fileName = '';
    this.validationResult = null;
    this.filePreview = [];
    this.confirmPermanent = false;
  }

  onFileSelected = async (event: any) => {
    const file = event.target.files[0];
    if (!file) return;
    this.processFile(file);
  }

  processFile = async (file: File) => {
    // Validar tipo de archivo
    const validExtensions = ['.xlsx', '.xls'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    
    if (!validExtensions.includes(fileExtension)) {
      alertifyjs.error('Solo se permiten archivos Excel (.xlsx o .xls)');
      return;
    }

    this.selectedFile = file;
    this.fileName = file.name;
    this.confirmPermanent = false;

    // Validar archivo
    await this.validateFile();
  }

  validateFile = async () => {
    if (!this.selectedFile) return;
    
    this.loadingValidation = true;
    try {
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      
      const response = await this._user.validateC004File(formData);
      this.validationResult = response.validation;
      
      if (this.validationResult.valid) {
        // Mostrar preview (primeras 10 filas máximo)
        const previewLimit = Math.min(10, this.validationResult.data.length);
        this.filePreview = this.validationResult.data.slice(0, previewLimit);
        alertifyjs.success('Archivo válido - Listo para procesar');
      } else {
        alertifyjs.error('El archivo contiene errores');
      }
    } catch (error: any) {
      console.error('Error validando archivo:', error);
      alertifyjs.error('Error al validar el archivo');
      this.validationResult = { valid: false, errors: ['Error al validar el archivo'] };
    }
    this.loadingValidation = false;
  }

  submitBatch = async () => {
    if (!this.selectedFile) return;
    
    this.loadingUpload = true;
    try {
      const formData = new FormData();
      formData.append('file', this.selectedFile);
      
      const response = await this._user.uploadC004Batch(formData);
      
      this.currentBatchId = response.batchId;
      this.currentTraceId = response.traceId;
      this.step = 3; // Ir a paso de procesamiento
      
      alertifyjs.success('Carga masiva iniciada correctamente');
      
      // Iniciar polling de progreso
      this.startProgressPolling();
      
    } catch (error: any) {
      console.error('Error procesando carga:', error);
      alertifyjs.error(error.error?.msg || 'Error al procesar la carga masiva');
    }
    this.loadingUpload = false;
  }

  backToUpload = () => {
    this.step = 2;
    this.selectedFile = null;
    this.fileName = '';
    this.validationResult = null;
    this.filePreview = [];
  }

  // ============================================================
  // PASO 4: PROGRESO
  // ============================================================

  startProgressPolling = () => {
    this.processingInterval = setInterval(async () => {
      await this.checkProgress();
    }, 2000); // Cada 2 segundos
  }

  checkProgress = async () => {
    try {
      const batch = await this._user.getC004BatchDetail(this.currentBatchId);
      
      this.progress = batch.progress?.percentage || 0;
      
      // Si completó o falló, detener polling
      if (batch.status === 'COMPLETED' || batch.status === 'ERROR') {
        if (this.processingInterval) {
          clearInterval(this.processingInterval);
        }
        
        this.batchResult = batch;
        this.step = 4; // Ir a resultado
        
        if (batch.status === 'COMPLETED') {
          alertifyjs.success('Carga masiva completada');
        } else {
          alertifyjs.error('Error en la carga masiva');
        }
      }
    } catch (error) {
      console.error('Error verificando progreso:', error);
    }
  }

  // ============================================================
  // PASO 5: RESULTADO
  // ============================================================

  downloadResult = async () => {
    try {
      window.open(this.batchResult.output_file_cloudinary_url, '_blank');
      alertifyjs.success('Descargando resultado...');
    } catch (error) {
      console.error('Error descargando resultado:', error);
      alertifyjs.error('Error al descargar el resultado');
    }
  }

  finishAndReset = () => {
    // Reiniciar todo el estado
    this.activeTab = 'carga';
    this.step = 1;
    this.confirmPermanent = false;
    this.selectedFile = null;
    this.fileName = '';
    this.validationResult = null;
    this.filePreview = [];
    this.currentBatchId = '';
    this.currentTraceId = '';
    this.progress = 0;
    this.batchResult = null;
    this.isDragging = false;
    
    alertifyjs.success('Vista reiniciada - Lista para nueva carga');
  }

  startNewUpload = () => {
    this.finishAndReset();
    // Recargar historial
    this.loadBatchHistory();
  }

  // ============================================================
  // HISTORIAL DE LOTES
  // ============================================================

  loadBatchHistory = async () => {
    this.loadingHistory = true;
    try {
      const filters: any = {
        page: this.currentPage,
        limit: 10
      };
      
      if (this.filterStartDate) filters.startDate = this.filterStartDate;
      if (this.filterEndDate) filters.endDate = this.filterEndDate;
      if (this.filterStatus) filters.status = this.filterStatus;
      
      const response = await this._user.getC004BatchList(filters);
      
      this.batches = response.batches;
      this.totalPages = response.totalPages;
      
    } catch (error) {
      console.error('Error cargando historial:', error);
      alertifyjs.error('Error al cargar el historial');
    }
    this.loadingHistory = false;
  }

  applyFilters = () => {
    this.currentPage = 1;
    this.loadBatchHistory();
  }

  clearFilters = () => {
    this.filterStartDate = '';
    this.filterEndDate = '';
    this.filterStatus = '';
    this.currentPage = 1;
    this.loadBatchHistory();
  }

  changePage = (page: number) => {
    this.currentPage = page;
    this.loadBatchHistory();
  }

  viewBatchDetail = async (batchId: string) => {
    try {
      const batch = await this._user.getC004BatchDetail(batchId);
      this.batchResult = batch;
      this.step = 5;
    } catch (error) {
      console.error('Error obteniendo detalle:', error);
      alertifyjs.error('Error al obtener el detalle del lote');
    }
  }

  downloadBatchResult = (batch: any) => {
    if (batch.output_file_cloudinary_url) {
      window.open(batch.output_file_cloudinary_url, '_blank');
    } else {
      alertifyjs.warning('El lote aún no tiene archivo de resultados');
    }
  }

  getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case 'COMPLETED': return 'badge bg-success';
      case 'PROCESSING': return 'badge bg-warning';
      case 'ERROR': return 'badge bg-danger';
      case 'PENDING': return 'badge bg-secondary';
      default: return 'badge bg-secondary';
    }
  }

  getStatusText = (status: string): string => {
    switch (status) {
      case 'COMPLETED': return 'Completado';
      case 'PROCESSING': return 'Procesando';
      case 'ERROR': return 'Error';
      case 'PENDING': return 'Pendiente';
      default: return status;
    }
  }
}
